import { z } from 'zod';
import { LocalizedStringSchema, TimestampFieldsSchema, SupportedLanguageSchema } from './common';

// ─── Industry Type ────────────────────────────────────────────────────────────
// ไม่ hardcode แค่ hotel — รองรับทุกประเภทธุรกิจ

export const IndustrySchema = z.enum([
  'hotel',           // โรงแรม, รีสอร์ท, เซอร์วิสอพาร์ตเมนต์
  'corporate',       // บริษัททั่วไป, B2B, ที่ปรึกษา, IT/Agency
  'clinic',          // คลินิก, โรงพยาบาล, ทันตกรรม
  'retail',          // ร้านค้า, ห้างสรรพสินค้า, E-Commerce, วัสดุก่อสร้าง
  'restaurant',      // ร้านอาหาร, คาเฟ่, บาร์
  'events',          // สถานที่จัดงาน, ศูนย์ประชุม, Co-Working Space
  'education',       // โรงเรียน, สถาบัน, คอร์สออนไลน์
  'association',     // สมาคม, สภาวิชาชีพ, องค์กรวิชาชีพ, หอการค้า
  'fitness',         // ฟิตเนส, ยิม, โยคะ
  'beauty',          // ร้านเสริมสวย, นวด, สปา
  'automotive',      // อู่ซ่อมรถ, ดีลเลอร์
  'real_estate',     // อสังหาริมทรัพย์
  'service_repair',  // ศูนย์บริการซ่อมบำรุง, บริการช่าง, ล้างแอร์
  'logistics',       // ธุรกิจขนส่ง, คลังสินค้า, พัสดุ
  'entertainment',   // สวนสนุก, คอนเสิร์ต, ตั๋วกิจกรรม, สนามกอล์ฟ
  'custom',          // ธุรกิจอื่นๆ (กำหนดเอง)
]);
export type Industry = z.infer<typeof IndustrySchema>;

// ─── Enabled Modules ──────────────────────────────────────────────────────────
// แต่ละ Tenant เลือก enable/disable module ได้

export const EnabledModulesSchema = z.object({
  book: z.boolean().default(false),       // Booking/Appointment
  ai: z.boolean().default(false),         // AI Chatbot + Knowledge Base
  crm: z.boolean().default(false),        // CRM & Customer Profiles
  catalog: z.boolean().default(false),    // Product/Service Catalog
  pay: z.boolean().default(false),        // Payments & Invoicing
  loyalty: z.boolean().default(false),    // Loyalty Points & Rewards
  ops: z.boolean().default(false),        // Operations (Housekeeping, Tasks)
  insights: z.boolean().default(false),   // Analytics & Dashboard
  campaigns: z.boolean().default(false),  // Email/Broadcast Campaigns
  connect: z.boolean().default(false),    // PMS, POS & Channel Manager Integration Hub
  support: z.boolean().default(false),    // Customer Support, Feedback & Ticket Hub
});
export type EnabledModules = z.infer<typeof EnabledModulesSchema>;

// ─── Enabled Channels ─────────────────────────────────────────────────────────

export const EnabledChannelsSchema = z.object({
  line: z.boolean().default(false),
  wechat: z.boolean().default(false),
  whatsapp: z.boolean().default(false),
  web: z.boolean().default(false),
  facebook: z.boolean().default(false),
});
export type EnabledChannels = z.infer<typeof EnabledChannelsSchema>;

// ─── Subscription Plan ───────────────────────────────────────────────────────

export const SubscriptionPlanSchema = z.enum(['free', 'starter', 'professional', 'enterprise']);
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

export interface TierLimitConfig {
  plan: SubscriptionPlan;
  name: string;
  maxRooms: number;
  maxAiMessagesPerMonth: number;
  maxChannels: number;
  maxStaffUsers: number;
  brandingBadge: boolean;
  brandingLink: string;
}

export const SUBSCRIPTION_TIER_LIMITS: Record<SubscriptionPlan, TierLimitConfig> = {
  free: {
    plan: 'free',
    name: 'Free Starter (25 ห้อง)',
    maxRooms: 25,
    maxAiMessagesPerMonth: 100,
    maxChannels: 1,
    maxStaffUsers: 2,
    brandingBadge: true,
    brandingLink: 'https://www.metagoldtech.com',
  },
  starter: {
    plan: 'starter',
    name: 'Starter (25 ห้อง)',
    maxRooms: 25,
    maxAiMessagesPerMonth: 500,
    maxChannels: 1,
    maxStaffUsers: 3,
    brandingBadge: false,
    brandingLink: 'https://www.metagoldtech.com',
  },
  professional: {
    plan: 'professional',
    name: 'Professional (50 ห้อง)',
    maxRooms: 50,
    maxAiMessagesPerMonth: 1500,
    maxChannels: 3,
    maxStaffUsers: 10,
    brandingBadge: false,
    brandingLink: 'https://www.metagoldtech.com',
  },
  enterprise: {
    plan: 'enterprise',
    name: 'Enterprise (ไม่จำกัด)',
    maxRooms: Infinity,
    maxAiMessagesPerMonth: 10000,
    maxChannels: 5,
    maxStaffUsers: 50,
    brandingBadge: false,
    brandingLink: 'https://www.metagoldtech.com',
  },
};

export function getSubscriptionTierLimits(plan?: string | null): TierLimitConfig {
  const normalized = (plan?.toLowerCase() || 'free') as SubscriptionPlan;
  return SUBSCRIPTION_TIER_LIMITS[normalized] || SUBSCRIPTION_TIER_LIMITS.free;
}

export const TenantStatusSchema = z.enum(['active', 'inactive', 'suspended', 'trial', 'cancelled', 'waiting_list', 'pending_approval', 'rejected']);
export type TenantStatus = z.infer<typeof TenantStatusSchema>;

// ─── Global Free Tier System Constraints (Platform-Wide Policy) ─────────────
export const GLOBAL_FREE_TIER_CONFIG = {
  maxActiveFreeTenants: 50,       // เพดานสูงสุด 50 โรงแรมทั่วระบบ
  inactivityThresholdDays: 30,    // ไม่มีความเคลื่อนไหว 30 วัน -> suspended & ให้คิว Waiting List
  maxRooms: 25,
  maxTables: 10,
  maxFnbOrdersPerMonth: 150,
  maxSpaBeds: 2,
  maxSpaBookingsPerMonth: 30,
  maxMiceRfpPerMonth: 15,
  maxAiMessagesPerMonth: 100,
  maxStaffUsers: 2,
  brandingLink: 'https://www.metagoldtech.com',
};

// ─── Custom Tier Limits Schema (Per Tenant Override & Guardrails) ───────────
export const TenantCustomTierLimitsSchema = z.object({
  enforceLimits: z.boolean().default(true),
  maxRooms: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.maxRooms),
  maxTables: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.maxTables),
  maxFnbOrdersPerMonth: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.maxFnbOrdersPerMonth),
  maxSpaBeds: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.maxSpaBeds),
  maxSpaBookingsPerMonth: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.maxSpaBookingsPerMonth),
  maxMiceRfpPerMonth: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.maxMiceRfpPerMonth),
  maxAiMessagesPerMonth: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.maxAiMessagesPerMonth),
  maxStaffUsers: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.maxStaffUsers),
  enforceInactivityRule: z.boolean().default(true),
  brandingBadge: z.boolean().default(true),
});
export type TenantCustomTierLimits = z.infer<typeof TenantCustomTierLimitsSchema>;

// ─── Platform Gateway Configuration (e.g. for @metagoldtech) ────────────────
export const PlatformGatewayConfigSchema = z.object({
  isFreeTierGateway: z.boolean().default(false), // อนุญาตให้ LINE OA / LIFF ของ Tenant นี้เปิดฟอร์มสมัคร Free Tier
  onboardingLiffId: z.string().optional(),       // LIFF ID เฉพาะสำหรับฟอร์มรับสมัคร
  maxGlobalFreeTenants: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.maxActiveFreeTenants),
  inactivityDays: z.number().int().default(GLOBAL_FREE_TIER_CONFIG.inactivityThresholdDays),
});
export type PlatformGatewayConfig = z.infer<typeof PlatformGatewayConfigSchema>;

// ─── Industry Pack Presets ───────────────────────────────────────────────────
// Bundle ของ modules ที่เหมาะกับแต่ละ industry

export const INDUSTRY_PACK_PRESETS: Record<Industry, Partial<EnabledModules>> = {
  hotel:          { book: true, ai: true, crm: true, catalog: true, pay: true, ops: true, insights: true, campaigns: true, connect: true, support: true },
  corporate:      { ai: true, crm: true, pay: true, campaigns: true, insights: true, connect: true, support: true },
  clinic:         { book: true, ai: true, crm: true, pay: true, insights: true, campaigns: true, connect: true, support: true },
  retail:         { ai: true, crm: true, catalog: true, pay: true, loyalty: true, insights: true, campaigns: true, connect: true, support: true },
  restaurant:     { book: true, catalog: true, pay: true, insights: true, connect: true, support: true },
  events:         { book: true, ai: true, crm: true, catalog: true, pay: true, ops: true, insights: true, campaigns: true, connect: true, support: true },
  education:      { book: true, ai: true, crm: true, catalog: true, pay: true, loyalty: true, campaigns: true, insights: true, support: true },
  association:    { book: true, ai: true, crm: true, catalog: true, pay: true, loyalty: true, campaigns: true, insights: true, support: true },
  fitness:        { book: true, crm: true, pay: true, loyalty: true, insights: true, support: true },
  beauty:         { book: true, crm: true, pay: true, loyalty: true, insights: true, support: true },
  automotive:     { book: true, crm: true, catalog: true, pay: true, insights: true, support: true },
  real_estate:    { crm: true, catalog: true, campaigns: true, insights: true, support: true },
  service_repair: { book: true, ai: true, crm: true, pay: true, ops: true, insights: true, support: true },
  logistics:      { ai: true, crm: true, pay: true, ops: true, insights: true, campaigns: true, support: true },
  entertainment:  { book: true, ai: true, crm: true, catalog: true, pay: true, loyalty: true, campaigns: true, insights: true, support: true },
  custom:         { book: true, ai: true, crm: true, catalog: true, pay: true, insights: true, connect: true, support: true },
};

// ─── F&B / Restaurant Dining Settings ───────────────────────────────────────

export const RestaurantFnbSettingsSchema = z.object({
  orderModes: z.object({
    dineIn: z.boolean().default(true),
    takeaway: z.boolean().default(true),
    roomDelivery: z.boolean().default(false),
  }).default({ dineIn: true, takeaway: true, roomDelivery: false }),
  paymentModes: z.object({
    payAtTable: z.boolean().default(true),
    promptpay: z.boolean().default(true),
    chargeToRoom: z.boolean().default(false),
  }).default({ payAtTable: true, promptpay: true, chargeToRoom: false }),
  tableCount: z.number().int().default(20),
  tables: z.array(z.string()).default([]),
  serviceChargePct: z.number().default(10),
  vatPct: z.number().default(7),
  autoAcceptOrders: z.boolean().default(false),
});
export type RestaurantFnbSettings = z.infer<typeof RestaurantFnbSettingsSchema>;

export function getEffectiveFnbSettings(
  settings?: Partial<RestaurantFnbSettings>,
  industry: Industry = 'hotel'
): RestaurantFnbSettings {
  const isHotel = industry === 'hotel';
  const defaultRoomAllowed = isHotel;

  return {
    orderModes: {
      dineIn: settings?.orderModes?.dineIn ?? true,
      takeaway: settings?.orderModes?.takeaway ?? true,
      roomDelivery: settings?.orderModes?.roomDelivery ?? defaultRoomAllowed,
    },
    paymentModes: {
      payAtTable: settings?.paymentModes?.payAtTable ?? true,
      promptpay: settings?.paymentModes?.promptpay ?? true,
      chargeToRoom: settings?.paymentModes?.chargeToRoom ?? defaultRoomAllowed,
    },
    tableCount: settings?.tableCount ?? 20,
    tables: settings?.tables ?? [],
    serviceChargePct: settings?.serviceChargePct ?? 10,
    vatPct: settings?.vatPct ?? 7,
    autoAcceptOrders: settings?.autoAcceptOrders ?? false,
  };
}

// ─── Tenant Schema ────────────────────────────────────────────────────────────
// Generic — ไม่มี hotelCode, ไม่มี pmsConfig บังคับ

export const TenantSchema = z
  .object({
    id: z.string(),
    code: z.string().regex(/^[a-z0-9_-]+$/),  // lowercase slug
    name: LocalizedStringSchema,
    industry: IndustrySchema.default('custom'),
    status: TenantStatusSchema.default('trial'),
    plan: SubscriptionPlanSchema.default('free'),

    // Modules — เปิด/ปิดได้ต่อ Tenant
    enabledModules: EnabledModulesSchema.default({}),

    // Channels — เลือก channel ที่ใช้
    enabledChannels: EnabledChannelsSchema.default({}),

    // F&B / Restaurant Dining Configuration
    fnbSettings: RestaurantFnbSettingsSchema.optional(),

    // Free Tier Quota Limits & Platform Gateway (for @metagoldtech & client tenants)
    customTierLimits: TenantCustomTierLimitsSchema.optional(),
    platformGateway: PlatformGatewayConfigSchema.optional(),
    lastActiveAt: z.string().nullable().optional(),
    waitingListQueueNumber: z.number().int().nullable().optional(),
    registeredVia: z.enum(['line', 'web', 'admin']).nullable().optional(),

    // Branding
    branding: z.object({
      logoUrl: z.string().url().optional(),
      primaryColor: z.string().optional(),
      displayName: LocalizedStringSchema.optional(),
    }).optional(),

    // Terms & Conditions Acceptance Audit Trail
    termsAccepted: z.boolean().optional(),
    termsAcceptedAt: z.string().optional(),
    termsVersion: z.string().optional(),
    termsAcceptedBy: z.object({
      uid: z.string().optional(),
      email: z.string().optional(),
      lineUserId: z.string().optional(),
      ip: z.string().optional(),
      channel: z.string().optional(),
    }).optional(),

    // Locale
    timezone: z.string().default('Asia/Bangkok'),
    defaultLanguage: SupportedLanguageSchema.default('th'),
    supportedLanguages: z.array(SupportedLanguageSchema).default(['th', 'en']),

    // Integration & Credentials (LINE, PMS, Group, Social Multi-Channel)
    hotelName: z.union([LocalizedStringSchema, z.string()]).optional(),
    lineChannelId: z.string().optional(),
    lineChannelSecret: z.string().optional(),
    lineAccessToken: z.string().optional(),
    liffId: z.string().optional(),
    facebookPageId: z.string().optional(),
    facebookAppId: z.string().optional(),
    facebookAccessToken: z.string().optional(),
    whatsappPhoneId: z.string().optional(),
    whatsappWabaId: z.string().optional(),
    whatsappAccessToken: z.string().optional(),
    whatsappVerifyToken: z.string().optional(),
    wechatAppId: z.string().optional(),
    wechatAppSecret: z.string().optional(),
    linkedinCompanyId: z.string().optional(),
    linkedinAccessToken: z.string().optional(),
    pms: z
      .object({
        endpoint: z.string().nullable().optional(),
        hotelCode: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    pmsEndpoint: z.string().nullable().optional(),
    pmsHotelCode: z.string().nullable().optional(),
    pmsCredentials: z.string().nullable().optional(),
    companyGroup: z.string().nullable().optional(),
    isActive: z.boolean().optional(),
    contactPerson: z.string().optional(),
    contactPhone: z.string().optional(),
    contactEmail: z.string().optional(),
    appliedAt: z.string().optional(),
    approvedAt: z.string().optional(),
    approvedBy: z.object({ uid: z.string(), email: z.string() }).optional(),
    rejectedAt: z.string().optional(),
    rejectedReason: z.string().optional(),
    applicant: z.record(z.any()).optional(),
    channels: z.record(z.any()).optional(),
    customFields: z.record(z.any()).optional(),
    maskedLineSecret: z.string().optional(),
    maskedLineToken: z.string().optional(),
    hasLineSecret: z.boolean().optional(),
    hasLineToken: z.boolean().optional(),
  })
  .merge(TimestampFieldsSchema)
  .passthrough();

export type Tenant = z.infer<typeof TenantSchema>;

export const CreateTenantInputSchema = TenantSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).passthrough();
export type CreateTenantInput = z.infer<typeof CreateTenantInputSchema>;

// ─── Free Tier Onboarding Application Schemas ──────────────────────────────
export const FreeTierApplicationInputSchema = z.object({
  gatewayTenantId: z.string().default('metagoldtech'), // tenantId ของ gateway ที่ฟอร์มเรียก เช่น metagoldtech
  hotelName: z.string().min(2, 'ชื่อที่พัก/โรงแรมต้องมีอย่างน้อย 2 ตัวอักษร'),
  code: z.string().regex(/^[a-z0-9_-]+$/, 'รหัส Tenant ต้องเป็นตัวพิมพ์เล็ก ตัวเลข หรือขีดกลางเท่านั้น').optional(),
  contactPerson: z.string().min(2, 'ชื่อผู้ติดต่อต้องมีอย่างน้อย 2 ตัวอักษร'),
  contactPhone: z.string().min(9, 'เบอร์โทรศัพท์ไม่ถูกต้อง'),
  contactEmail: z.string().email('อีเมลไม่ถูกต้อง'),
  roomCount: z.number().int().min(1).max(GLOBAL_FREE_TIER_CONFIG.maxRooms, `Free tier รองรับไม่เกิน ${GLOBAL_FREE_TIER_CONFIG.maxRooms} ห้อง`),
  province: z.string().optional(),
  lineUserId: z.string().optional(),
  lineDisplayName: z.string().optional(),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'กรุณากดยอมรับข้อกำหนดและเงื่อนไขการใช้บริการ Free Tier ก่อนส่งใบสมัคร',
  }),
});
export type FreeTierApplicationInput = z.infer<typeof FreeTierApplicationInputSchema>;

export const FreeTierApplicationResultSchema = z.object({
  success: z.boolean(),
  tenantId: z.string(),
  status: TenantStatusSchema,
  message: z.string(),
  queueNumber: z.number().int().optional(),
  activeCount: z.number().int(),
  maxActive: z.number().int(),
  slotsRemaining: z.number().int(),
  estimatedActivation: z.string().optional(),
  adminLoginUrl: z.string().optional(),
});
export type FreeTierApplicationResult = z.infer<typeof FreeTierApplicationResultSchema>;
